import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { RcddbService } from '@core/services/rcddb.service';

@Component({
  standalone: false,
  selector: 'app-chip',
  templateUrl: './chip.component.html',
  styleUrls: ['./chip.component.scss']
})

export class ChipComponent implements OnInit{
  chips: string[] = [];
  inputText: string = '';
  editedChipText: string = '';
  editingChipIndex: number | null = null;
  isValidFromAPI: boolean = true;
  chipValidityMap: Map<string, boolean> = new Map(); 
  @ViewChild('input') inputElement: ElementRef;
  @Output() chipsChanged = new EventEmitter<boolean>();
  @Output() chipsUpdated = new EventEmitter<any>();
  @Input() inputChips = [];
  
  constructor(
    private rcddbService: RcddbService,
  ) {}

  ngOnInit(): void {
    this.chips = this.inputChips;
    this.initializeChipValidity();
  }

  private initializeChipValidity(): void {
    this.chips.forEach((chip) => {
      this.rcddbService.fetchfromAPI([chip]).subscribe((isValid: boolean) => {
        this.chipValidityMap.set(chip, isValid);
        this.updateOverallStatus();
      });
    });
  }

  isValid(chip: string): boolean {
    const isLengthValid = chip.length === 10 && /^[0-9]+$/.test(chip);
  return isLengthValid && this.chipValidityMap.get(chip) === true;
  }

  addChip() {
    const trimmedText = this.inputText.trim();
    if (trimmedText && !this.chips.includes(trimmedText) && trimmedText !== ',') {
      this.chips.push(trimmedText);
      this.validateNewChips([trimmedText]);
    } else {
      this.updateOverallStatus();
    }
    setTimeout( () => {
      this.inputText = '';
      this.inputElement.nativeElement.value = '';
    });
  }

  handlePaste(event) {
    const pastedText = event.clipboardData.getData('text/plain')
    const newlyAdded: string[] = [];
    let splitByComma = pastedText.replace(/\n/g, ' ').split(',');
    splitByComma.forEach(element => {
      if(element.length) {
        element.split(' ').forEach(value => {
          if(value !== '') {
            const trimmedText = value.trim();
            if (trimmedText && !this.chips.includes(trimmedText) && trimmedText !== ',') {
              this.chips.push(trimmedText);
              newlyAdded.push(trimmedText);
            }
          }
        })
      }
    });
    setTimeout( () => {
      this.inputText = '';
      this.inputElement.nativeElement.value = '';
    });
    if (newlyAdded.length) {
      this.validateNewChips(newlyAdded);
    } else {
      this.updateOverallStatus();
    }
  }

  removeChip(index: number) {
    const removed = this.chips.splice(index, 1);
    if (removed.length) {
      this.chipValidityMap.delete(removed[0]);
    }
    this.updateOverallStatus();
  }

  startEditing(index: number, chip: string) {
    this.editingChipIndex = index;
    this.editedChipText = chip;
    setTimeout(() => {
      const input = document.querySelector('.chip-edit-input') as HTMLInputElement;
      input?.focus();
    }, 0);
  }

  finishEditing(index: number) {
    const trimmed = this.editedChipText.trim();
    if (trimmed) {
      const oldChip = this.chips[index];
      if (oldChip !== trimmed) {
        this.chipValidityMap.delete(oldChip);
        this.chips[index] = trimmed;
        this.editingChipIndex = null;
        this.validateNewChips([trimmed]);
        return;
      }
    }
    this.editingChipIndex = null;
    this.updateOverallStatus();
  }

  cancelEditing() {
    this.editingChipIndex = null;
    this.updateOverallStatus();
  }

  onBackspace(event) {
    if (!this.inputText && !this.editingChipIndex) {
      const removed = this.chips.pop();
      if (removed) {
        this.chipValidityMap.delete(removed);
      }
    }
    this.updateOverallStatus();
  }

  focusInput() {
    if (!this.editingChipIndex) {
      const input = this.inputElement.nativeElement;
      input.focus();
    }
  }

  private updateOverallStatus(): void {
    const allGreen = this.chips.every((chip) => this.isValid(chip));
    this.chipsChanged.emit(allGreen);
    if (allGreen) {
      this.chipsUpdated.emit(this.chips);
    }
  }

  private validateNewChips(newChips: string[]): void {
    newChips.forEach((chip) => {
      this.rcddbService.fetchfromAPI([chip]).subscribe((isValid: boolean) => {
        this.chipValidityMap.set(chip, isValid);
        this.updateOverallStatus();
      });
    });
    this.updateOverallStatus();
  }
}
